import { createClient } from '@/lib/utils/supabase/client'
import { Cart, CartItem, AddToCartRequest } from '@/types/cart'
import { Logger } from '@/lib/logger'
import { TourRoom } from '@/types/tour'

const supabase = createClient();

export class CartError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CartError'
  }
}

class CartService {
  private readonly CART_TABLE = 'carts'
  private readonly CART_ITEMS_TABLE = 'cart_items'

  async getCart(): Promise<Cart | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      // Return null if user is not authenticated
      if (!session?.user) {
        return null
      }

      const cart = await this.getOrCreateCart()
      if (!cart) return null

      const { data: items, error: itemsError } = await supabase
        .from(this.CART_ITEMS_TABLE)
        .select(`
          id,
          cart_id,
          tour_id,
          room_type,
          number_of_beds,
          price_per_person,
          price_snapshot,
          created_at,
          updated_at,
          tour:tours (
            id,
            title,
            tour_type,
            package_type,
            start_date,
            end_date,
            destinations,
            payment_details,
            service_details,
            hotels,
            company_id,
            company:companies!company_id (
              id,
              company_name
            )
          )
        `)
        .eq('cart_id', cart.id)

      if (itemsError) throw itemsError

      const transformedItems: CartItem[] = (items || []).map((item: any) => {
        let tourData = item.tour
        if (Array.isArray(tourData)) {
          tourData = tourData[0]
        }
        if (tourData && Array.isArray(tourData.company)) {
          tourData.company = tourData.company[0]
        }
        return {
          ...item,
          tour: tourData,
        }
      })

      return {
        ...cart,
        items: transformedItems,
      }
    } catch (error) {
      Logger.error('Error in getCart:', error)
      throw error
    }
  }

  async addToCart(request: AddToCartRequest): Promise<CartItem> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      // Throw error if user is not authenticated
      if (!session?.user) {
        throw new CartError('User must be authenticated to add items to cart')
      }

      const cart = await this.getOrCreateCart()

      // First, get the room price
      const { data: room, error: roomError } = await supabase
        .from('tour_rooms')
        .select('price_per_person')
        .eq('tour_id', request.tourId)
        .eq('room_type', request.roomType)
        .single()

      if (roomError) throw roomError
      if (!room) throw new Error('Room not found')

      const { data: existingItem } = await supabase
        .from(this.CART_ITEMS_TABLE)
        .select()
        .eq('cart_id', cart.id)
        .eq('tour_id', request.tourId)
        .eq('room_type', request.roomType)
        .maybeSingle()

      if (existingItem) {
        const { data: updatedItem } = await supabase
          .from(this.CART_ITEMS_TABLE)
          .update({
            number_of_beds: existingItem.number_of_beds + request.numberOfBeds
          })
          .eq('id', existingItem.id)
          .select()
          .single()

        return updatedItem!
      } else {
        const { error: insertError } = await supabase
          .from(this.CART_ITEMS_TABLE)
          .insert({
            cart_id: cart.id,
            tour_id: request.tourId,
            room_type: request.roomType,
            number_of_beds: request.numberOfBeds,
            price_per_person: room.price_per_person,
            price_snapshot: {
              base_price: room.price_per_person,
              timestamp: new Date().toISOString(),
              currency: 'USD'
            }
          })

        if (insertError) throw insertError
      }

      // Update cart metadata
      await supabase
        .from(this.CART_TABLE)
        .update({
          updated_at: new Date().toISOString(),
          metadata: {
            last_activity: new Date().toISOString(),
            device_info: this.getDeviceInfo()
          }
        })
        .eq('id', cart.id)

      const updatedCart = await this.getCart()
      if (!updatedCart) throw new CartError('Failed to get updated cart')
      return updatedCart.items![0]
    } catch (error) {
      Logger.error('Error in addToCart:', error)
      throw error
    }
  }

  private async getOrCreateCart(): Promise<Cart> {
    try {
      const { data: { session } } = await supabase.auth.getSession()

      // Throw error if user is not authenticated
      if (!session?.user) {
        throw new CartError('User must be authenticated to access cart')
      }

      let query = supabase
        .from(this.CART_TABLE)
        .select('*')
        .eq('status', 'ACTIVE')
        .eq('customer_id', session.user.id)

      let { data: cart, error } = await query.maybeSingle()

      if (error) throw error

      if (!cart) {
        const { data: newCart, error: createError } = await supabase
          .from(this.CART_TABLE)
          .insert({
            customer_id: session.user.id,
            cart_type: 'AUTHENTICATED',
            status: 'ACTIVE',
            expires_at: this.calculateExpiryTime(),
            metadata: {
              last_activity: new Date().toISOString(),
              device_info: this.getDeviceInfo()
            }
          })
          .select()
          .single()

        if (createError) throw createError
        cart = newCart
      }

      return cart
    } catch (error) {
      Logger.error('Error in getOrCreateCart:', error)
      throw new CartError('Failed to get or create cart')
    }
  }

  private calculateExpiryTime(): string {
    const expiryDate = new Date()
    expiryDate.setDate(expiryDate.getDate() + 7) // Cart expires in 7 days
    return expiryDate.toISOString()
  }

  private getDeviceInfo(): Record<string, string> {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language
    }
  }

  async removeFromCart(itemId: number): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.CART_ITEMS_TABLE)
        .delete()
        .eq('id', itemId)

      if (error) throw error
    } catch (error) {
      Logger.error('Error in removeFromCart:', error)
      throw error
    }
  }

  async updateItemQuantity(itemId: number, numberOfBeds: number): Promise<void> {
    try {
      const { error } = await supabase
        .from('cart_items')
        .update({ number_of_beds: numberOfBeds })
        .eq('id', itemId)

      if (error) throw error
    } catch (error) {
      Logger.error('Error in updateItemQuantity:', error)
      throw error
    }
  }

  async updateCartItem(itemId: number, newQuantity: number): Promise<CartItem> {
    const { data, error } = await supabase
      .from(this.CART_ITEMS_TABLE)
      .update({ number_of_beds: newQuantity })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
}

export const cartService = new CartService()